'use client';

import { useTranslations } from 'next-intl';
import { Icon, IconType } from '@/components/Icon';
import type { TableOfContentsEntry } from '@/lib/magazine/extractTableOfContents';

interface ArticleTableOfContentsProps {
  entries: TableOfContentsEntry[];
}

/**
 * Fixed rather than `useId()`: one article renders one contents list. Not
 * `inhalt` — a section titled "Inhalt" slugifies to exactly that, and only
 * `autor` is reserved, so the two ids would collide.
 */
const HEADING_ID = 'article-table-of-contents';

/**
 * The `Inhalt` list, beside the hero image and below the intro. Derived
 * server-side from blocks the server already resolved, so it is in the initial
 * HTML and costs no extra request.
 *
 * The anchors come from the extractor, never from the titles here — following
 * an entry has to reach the block that carries the identical `anchorId`.
 */
export const ArticleTableOfContents = ({
  entries,
}: ArticleTableOfContentsProps) => {
  const t = useTranslations('magazine');

  if (entries.length === 0) {
    return null;
  }

  return (
    // Named by its own visible heading, so the landmark is announced as
    // "Inhalt" rather than an anonymous navigation.
    <nav aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className="mb-3 text-lg font-semibold">
        {t('tableOfContents')}
      </h2>
      {/* `pl-0` clears the user-agent list indent so the entries start
          flush with the "Inhalt" headline above them. */}
      <ol role="list" className="flex list-none flex-col pl-0">
        {entries.map((entry) => (
          <li
            key={entry.anchor}
            className="flex items-start gap-2 leading-snug"
          >
            <Icon
              src={IconType.SvgCheckmark}
              size={20}
              className="text-primary mt-0.5 shrink-0"
            />
            <a
              href={`#${entry.anchor}`}
              className="text-primary hover:underline"
            >
              {entry.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};
